import { createSubscription } from '@gonfalon/openapi/operations/createSubscription';
import { deleteSubscription } from '@gonfalon/openapi/operations/deleteSubscription';
import { getConfigurations } from '@gonfalon/openapi/operations/getConfigurations';
import { getSubscriptionByID } from '@gonfalon/openapi/operations/getSubscriptionByID';
import { getSubscriptions } from '@gonfalon/openapi/operations/getSubscriptions';
import { updateSubscription } from '@gonfalon/openapi/operations/updateSubscription';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getSubscriptionsQuery = createQueryOptions(getSubscriptions);

export const getSubscriptionQuery = createQueryOptions(getSubscriptionByID);

/**
 * Every integration's subscriptions in one request, rather than fanning `getSubscriptionsQuery` out
 * across manifests. The server already assembles them concurrently
 * (`internal/api2/integrations/root_get.go`), returning one collection per events-hook integration.
 *
 * Per-item shape matches `getSubscriptionsQuery` — both handlers use
 * `reps2.MakeIntegrationSubscriptionRepCollection` — and each collection carries its `key` to index
 * by.
 */
export const getAllIntegrationSubscriptionsQuery = createQueryOptions(getConfigurations);

/**
 * Scoped by `integrationKey` deliberately: the page holds one subscription query per manifest, so an
 * unscoped invalidation refetches every integration on a single edit.
 */
async function invalidateSubscriptions(integrationKey: string) {
  const queryClient = getQueryClient();
  await queryClient.invalidateQueries({ queryKey: getSubscriptionsQuery.partialQueryKey({ integrationKey }) });
  // The bulk read covers every integration, so it cannot be scoped by key — any subscription change
  // makes it stale. Without this the catalog keeps showing old counts and configured state.
  await queryClient.invalidateQueries({ queryKey: getAllIntegrationSubscriptionsQuery.partialQueryKey() });
}

/**
 * Invalidates only the bulk read, for the legacy Redux write path in `#actions/integrations`, which
 * still owns subscription mutations while the integrations page is mid-migration (REL-15378).
 *
 * Separate from `invalidateSubscriptions` because a `GoaltenderSubscription` record carries no
 * integration key to scope by, and the bulk read is the only one with live consumers. Any subscription
 * change makes it stale; without this the plain catalog keeps showing old counts and configured state.
 */
export async function invalidateAllIntegrationSubscriptions() {
  const queryClient = getQueryClient();
  await queryClient.invalidateQueries({ queryKey: getAllIntegrationSubscriptionsQuery.partialQueryKey() });
}

export const useCreateSubscription = createMutationHook(createSubscription, {
  onSuccess: async (_data, { integrationKey }) => invalidateSubscriptions(integrationKey),
});

export const useUpdateSubscription = createMutationHook(updateSubscription, {
  onSuccess: async (_data, { integrationKey, id }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getSubscriptionQuery.partialQueryKey({ integrationKey, id }) });
    await invalidateSubscriptions(integrationKey);
  },
});

export const useDeleteSubscription = createMutationHook(deleteSubscription, {
  onSuccess: async (_data, { integrationKey, id }) => {
    const queryClient = getQueryClient();
    queryClient.removeQueries({ queryKey: getSubscriptionQuery.partialQueryKey({ integrationKey, id }) });
    await invalidateSubscriptions(integrationKey);
  },
});
